"use client";

import { useState, useCallback, useRef } from "react";
import { api } from "~/trpc/react";
import { useDiscount } from "~/hooks/use-discount";
import type {
  CreatePaymentIntentOptions as PaymentIntentOptions,
  CreateRelationshipPaymentIntentOptions as RelationshipPaymentIntentOptions,
  PaymentMetadata,
} from "~/types/payment";

type CreatePaymentIntentMutation = ReturnType<
  typeof api.paymentIntent.createPaymentIntent.useMutation
>;
type CreatePaymentIntentResult = Awaited<
  ReturnType<CreatePaymentIntentMutation["mutateAsync"]>
>;
type CreateRelationshipPaymentIntentMutation = ReturnType<
  typeof api.paymentIntent.createRelationshipPaymentIntent.useMutation
>;
type CreateRelationshipPaymentIntentResult = Awaited<
  ReturnType<CreateRelationshipPaymentIntentMutation["mutateAsync"]>
>;

const inFlightPaymentIntentRequests = new Map<
  string,
  Promise<CreatePaymentIntentResult>
>();
const inFlightRelationshipPaymentIntentRequests = new Map<
  string,
  Promise<CreateRelationshipPaymentIntentResult>
>();

function buildPaymentIntentRequestKey(
  options: PaymentIntentOptions,
  discountCode: string | null | undefined,
): string {
  return JSON.stringify({
    therapistId: options.therapistId,
    therapyType: options.therapyType,
    sessionTimestamp: options.sessionTimestamp ?? null,
    sessionQuantity: options.sessionQuantity ?? 1,
    discountCode: discountCode ?? null,
    attributionBridgeId: options.attributionBridgeId ?? null,
    proposalMessageId: options.proposalMessageId ?? null,
  });
}

function buildRelationshipPaymentIntentRequestKey(
  options: RelationshipPaymentIntentOptions,
  discountCode: string | null | undefined,
): string {
  return JSON.stringify({
    therapyRelationshipId: options.therapyRelationshipId,
    sessionTimestamp: options.sessionTimestamp ?? null,
    sessionQuantity: options.sessionQuantity ?? 1,
    discountCode: discountCode ?? null,
    attributionBridgeId: options.attributionBridgeId ?? null,
  });
}

export function usePaymentIntent() {
  const requestVersionRef = useRef(0);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentMetadata, setPaymentMetadata] =
    useState<PaymentMetadata | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<{
    creditUsed: number;
    amountToCharge: number;
    creditBalance: number;
    totalAmount: number;
  } | null>(null);

  const { discountCode, isValidating } = useDiscount();

  const createPaymentIntentMutation =
    api.paymentIntent.createPaymentIntent.useMutation();
  const createRelationshipPaymentIntentMutation =
    api.paymentIntent.createRelationshipPaymentIntent.useMutation();
  const createPaymentIntentAsync = createPaymentIntentMutation.mutateAsync;
  const createRelationshipPaymentIntentAsync =
    createRelationshipPaymentIntentMutation.mutateAsync;
  const createPaymentIntent = useCallback(
    async (options: PaymentIntentOptions) => {
      const requestVersion = ++requestVersionRef.current;

      try {
        setError(null);
        const requestKey = buildPaymentIntentRequestKey(options, discountCode);
        let requestPromise = inFlightPaymentIntentRequests.get(requestKey);

        if (!requestPromise) {
          requestPromise = createPaymentIntentAsync({
            therapistId: options.therapistId,
            therapyType: options.therapyType,
            sessionTimestamp: options.sessionTimestamp,
            discountCode: discountCode || undefined,
            sessionQuantity: options.sessionQuantity ?? 1,
            ...(options.proposalMessageId != null
              ? { proposalMessageId: options.proposalMessageId }
              : {}),
            ...(options.attributionBridgeId
              ? { attributionBridgeId: options.attributionBridgeId }
              : {}),
          }).finally(() => {
            inFlightPaymentIntentRequests.delete(requestKey);
          });
          inFlightPaymentIntentRequests.set(requestKey, requestPromise);
        }

        const result = await requestPromise;

        if (requestVersion !== requestVersionRef.current) {
          return result;
        }

        setClientSecret(result.clientSecret ?? null);
        setPaymentIntentId(result.paymentIntentId);
        const amountToCharge =
          typeof result.amountToCharge === "number"
            ? result.amountToCharge
            : result.amount;
        const creditUsed =
          typeof result.creditUsed === "number" ? result.creditUsed : 0;
        const creditBalance =
          typeof result.creditBalance === "number" ? result.creditBalance : 0;
        setPaymentSummary({
          creditUsed,
          amountToCharge,
          creditBalance,
          totalAmount: result.amount,
        });

        // Store metadata for PayPal
        const attributionBridgeId =
          result.attributionBridgeId ?? options.attributionBridgeId;
        setPaymentMetadata({
          amount: amountToCharge,
          conversionValue: result.amount,
          paymentId: result.paymentIntentId ?? undefined,
          therapistId: options.therapistId,
          therapyType: options.therapyType,
          sessionTimestamp: options.sessionTimestamp,
          sessionQuantity: options.sessionQuantity ?? 1,
          trackingEventName: result.trackingEventName,
          ...(options.proposalMessageId != null
            ? { proposalMessageId: options.proposalMessageId }
            : {}),
          ...(attributionBridgeId ? { attributionBridgeId } : {}),
        });

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to create payment intent";
        if (requestVersion === requestVersionRef.current) {
          setError(errorMessage);
        }
        throw err;
      }
    },
    [createPaymentIntentAsync, discountCode],
  );

  const createRelationshipPaymentIntent = useCallback(
    async (options: RelationshipPaymentIntentOptions) => {
      const requestVersion = ++requestVersionRef.current;

      try {
        setError(null);
        const requestKey = buildRelationshipPaymentIntentRequestKey(
          options,
          discountCode,
        );
        let requestPromise =
          inFlightRelationshipPaymentIntentRequests.get(requestKey);

        if (!requestPromise) {
          requestPromise = createRelationshipPaymentIntentAsync({
            therapyRelationshipId: options.therapyRelationshipId,
            sessionTimestamp: options.sessionTimestamp,
            discountCode: discountCode || undefined,
            sessionQuantity: options.sessionQuantity ?? 1,
            ...(options.attributionBridgeId
              ? { attributionBridgeId: options.attributionBridgeId }
              : {}),
          }).finally(() => {
            inFlightRelationshipPaymentIntentRequests.delete(requestKey);
          });
          inFlightRelationshipPaymentIntentRequests.set(
            requestKey,
            requestPromise,
          );
        }

        const result = await requestPromise;

        if (requestVersion !== requestVersionRef.current) {
          return result;
        }

        setClientSecret(result.clientSecret ?? null);
        setPaymentIntentId(result.paymentIntentId);
        const amountToCharge =
          typeof result.amountToCharge === "number"
            ? result.amountToCharge
            : result.amount;
        const creditUsed =
          typeof result.creditUsed === "number" ? result.creditUsed : 0;
        const creditBalance =
          typeof result.creditBalance === "number" ? result.creditBalance : 0;
        setPaymentSummary({
          creditUsed,
          amountToCharge,
          creditBalance,
          totalAmount: result.amount,
        });

        const attributionBridgeId =
          result.attributionBridgeId ?? options.attributionBridgeId;
        setPaymentMetadata({
          amount: amountToCharge,
          conversionValue: result.amount,
          paymentId: result.paymentIntentId ?? undefined,
          therapyRelationshipId: options.therapyRelationshipId,
          therapistId: result.therapistId,
          therapyType: result.therapyType,
          sessionTimestamp: options.sessionTimestamp,
          sessionQuantity: options.sessionQuantity ?? 1,
          trackingEventName: result.trackingEventName,
          ...(attributionBridgeId ? { attributionBridgeId } : {}),
        });

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to create payment intent";
        if (requestVersion === requestVersionRef.current) {
          setError(errorMessage);
        }
        throw err;
      }
    },
    [createRelationshipPaymentIntentAsync, discountCode],
  );

  const resetPaymentIntent = useCallback(() => {
    requestVersionRef.current += 1;
    setClientSecret(null);
    setPaymentIntentId(null);
    setError(null);
    setPaymentMetadata(null);
    setPaymentSummary(null);
  }, []);

  return {
    clientSecret,
    paymentIntentId,
    paymentMetadata,
    paymentSummary,
    error,
    isLoading:
      createPaymentIntentMutation.isPending ||
      createRelationshipPaymentIntentMutation.isPending,
    isValidatingDiscount: isValidating,
    createPaymentIntent,
    createRelationshipPaymentIntent,
    resetPaymentIntent,
  };
}
